import * as React from 'react';
import { QueryClient, QueryClientProvider, useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { RpcOk } from '@phinest/shared';
import { RpcError, callTool, toolData } from './rpc';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => {
        // Never retry a decision the server already made.
        if (error instanceof RpcError && error.status >= 400 && error.status < 500) return false;
        return count < 2;
      },
    },
  },
});

export function QueryProvider({ children }: { children: React.ReactNode }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

export const toolKey = (tool: string, args?: Record<string, unknown>) => ['rpc', tool, args ?? {}] as const;

/**
 * Read through a tool. Returns the tool's `data` payload.
 *
 * Some tools wrap their result (`list_services` → `{ services }`), so pass
 * `select` to unwrap it and give the second generic the unwrapped type:
 *   useTool<{ services: S[] }, S[]>('list_services', {}, { select: (d) => d.services })
 */
export function useTool<T = unknown, TSelected = T>(
  tool: string,
  args: Record<string, unknown> = {},
  options: Omit<UseQueryOptions<T, RpcError, TSelected>, 'queryKey' | 'queryFn'> = {},
) {
  return useQuery<T, RpcError, TSelected>({
    queryKey: toolKey(tool, args),
    queryFn: ({ signal }) => toolData<T>(tool, args, { signal }),
    ...options,
  });
}

export interface MutateVars {
  args?: Record<string, unknown>;
  dryRun?: boolean;
  confirmToken?: string;
}

/**
 * Write through a tool. By default every cached read is invalidated afterwards,
 * which is the right trade-off at this data scale and keeps totals honest
 * (an invoice write changes customer balances, reports and the dashboard).
 */
export function useToolMutation<T = unknown>(tool: string, opts: { invalidate?: boolean; onSuccess?: (r: RpcOk<T>) => void } = {}) {
  const qc = useQueryClient();
  return useMutation<RpcOk<T>, RpcError, MutateVars>({
    mutationFn: (vars) => callTool<T>(tool, vars.args ?? {}, { dryRun: vars.dryRun, confirmToken: vars.confirmToken }),
    onSuccess: (result, vars) => {
      const wrotePersistently = !result.preview && !vars.dryRun;
      if (wrotePersistently && (opts.invalidate ?? true)) void qc.invalidateQueries({ queryKey: ['rpc'] });
      opts.onSuccess?.(result);
    },
  });
}

/** Invalidate every tool read. */
export function useRefreshAll() {
  const qc = useQueryClient();
  return React.useCallback(() => void qc.invalidateQueries({ queryKey: ['rpc'] }), [qc]);
}
